import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, ScrollText, RefreshCw } from 'lucide-react';
import { getAuditLog } from '../../../services/apiClient';
import { Button } from '../../../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';

const ALL = '__all__';

// Event types this admin console (or the access-control layer it sits on
// top of) can actually write to public.audit_log today — see
// shared/audit_log.py's record_denial call sites and the new record_action
// call sites added alongside this panel.
const EVENT_TYPES = [
  'admin_denied',
  'file_type_denied',
  'etl_proxy_denied',
  'role_granted',
  'role_revoked',
  'tenant_deactivated',
  'tenant_reactivated',
  'cache_cleared_all',
];

/**
 * Global "Audit Log" view of the tenant admin console — a filterable read
 * of public.audit_log (access denials + deliberate admin actions like role
 * grants/revokes, tenant deactivation, and a global cache clear). Backed by
 * GET /admin/tenants/audit-log.
 */
const AuditLogPanel = ({ customers }) => {
  const { t } = useTranslation('admin');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [eventType, setEventType] = useState('');

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      setEntries(await getAuditLog({ customerId: customerId || undefined, eventType: eventType || undefined }));
    } catch (e) {
      setErr(t('errors.listAuditLog', { detail: e.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, eventType]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
          <ScrollText className="w-4 h-4" /> {t('auditLog.title')}
        </h2>
        <Button size="sm" variant="outline" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {t('auditLog.refresh')}
        </Button>
      </div>
      <div className="flex gap-2 mb-3">
        <Select value={customerId || ALL} onValueChange={(v) => setCustomerId(v === ALL ? '' : v)}>
          <SelectTrigger className="h-8 text-xs w-48">
            <SelectValue placeholder={t('auditLog.allTenants')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('auditLog.allTenants')}</SelectItem>
            {customers.map((c) => (
              <SelectItem key={c.customer_id} value={c.customer_id}>
                {c.display_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={eventType || ALL} onValueChange={(v) => setEventType(v === ALL ? '' : v)}>
          <SelectTrigger className="h-8 text-xs w-56">
            <SelectValue placeholder={t('auditLog.allEvents')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('auditLog.allEvents')}</SelectItem>
            {EVENT_TYPES.map((e) => (
              <SelectItem key={e} value={e}>
                {e}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {err && <div className="mb-3 text-sm text-error-600">{err}</div>}
      {loading && <Loader className="w-4 h-4 animate-spin text-gray-400 mb-2" />}
      {!loading && entries.length === 0 && <div className="text-sm text-gray-400">{t('auditLog.noEntries')}</div>}
      {entries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-1.5 pr-3 font-medium">{t('auditLog.columns.time')}</th>
                <th className="py-1.5 pr-3 font-medium">{t('auditLog.columns.event')}</th>
                <th className="py-1.5 pr-3 font-medium">{t('auditLog.columns.user')}</th>
                <th className="py-1.5 pr-3 font-medium">{t('auditLog.columns.tenant')}</th>
                <th className="py-1.5 font-medium">{t('auditLog.columns.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-gray-100 align-top">
                  <td className="py-1.5 pr-3 whitespace-nowrap text-gray-500">{entry.occurred_at}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap font-medium text-gray-900">{entry.event_type}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-gray-600">
                    {entry.user_email || entry.user_id || '—'}
                  </td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-gray-600">{entry.customer_id || '—'}</td>
                  <td className="py-1.5 text-gray-500 font-mono break-all">{JSON.stringify(entry.detail)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
